import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToHostReservationRequests,
  ShortstayRequest,
} from "../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../redux/authSlice";
import { listenToShortstayData } from "../../../redux/shortstaysSlice";
import {
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Check, X, Clock, Calendar, MapPin, ChevronDown, ChevronUp } from "lucide-react";

/* ── tokens — white/black only ── */
const T = {
  bg:          "#F7F7F7",
  surface:     "#ffffff",
  border:      "#E8E8E8",
  border2:     "#CCCCCC",
  text:        "#111111",
  textSoft:    "#444444",
  muted:       "#888888",
  sans:        "'Montserrat', sans-serif",
  serif:       "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; }
`;

const parseISODate = (iso: string): Date => {
  if (!iso) return new Date();
  const clean = iso.split("T")[0];
  const [y, m, d] = clean.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDate = (iso: string) =>
  parseISODate(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });

const diffNights = (a: string, b: string) => {
  const ms = parseISODate(b).getTime() - parseISODate(a).getTime();
  return Math.round(ms / 86400000);
};

const approveRequest = async (requestId: string) => {
  const ref = doc(db, "ShortstayRequests", requestId);
  await updateDoc(ref, {
    isPending:  false,
    isApproved: true,
    isRejected: false,
    updatedAt:  serverTimestamp(),
  });
};

const declineRequest = async (requestId: string) => {
  const ref = doc(db, "ShortstayRequests", requestId);
  await updateDoc(ref, {
    isPending:  false,
    isApproved: false,
    isRejected: true,
    updatedAt:  serverTimestamp(),
  });
};

/* ── 3-D tab active styles ── */
const tabActiveStyles: Record<string, React.CSSProperties> = {
  pending: {
    border: "none",
    background: "linear-gradient(145deg, #E64A19 0%, #D32F2F 60%, #9B1B1B 100%)",
    color: "#fff",
    boxShadow: `
      0 1px 0 1px rgba(255,255,255,0.32) inset,
      0 -1px 0 1px rgba(0,0,0,0.22) inset,
      0 4px 8px rgba(211,47,47,0.45),
      0 8px 16px rgba(211,47,47,0.2)
    `,
    textShadow: "0 1px 2px rgba(0,0,0,0.35)",
    transform: "translateY(-1px)",
  },
  approved: {
    border: "none",
    background: "linear-gradient(145deg, #FF8F00 0%, #E65100 60%, #BF360C 100%)",
    color: "#fff",
    boxShadow: `
      0 1px 0 1px rgba(255,255,255,0.28) inset,
      0 -1px 0 1px rgba(0,0,0,0.2) inset,
      0 4px 8px rgba(230,81,0,0.4),
      0 8px 16px rgba(230,81,0,0.18)
    `,
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
    transform: "translateY(-1px)",
  },
  declined: {
    border: "none",
    background: "linear-gradient(145deg, #757575 0%, #616161 60%, #424242 100%)",
    color: "#fff",
    boxShadow: `
      0 1px 0 1px rgba(255,255,255,0.2) inset,
      0 -1px 0 1px rgba(0,0,0,0.2) inset,
      0 4px 8px rgba(97,97,97,0.35),
      0 8px 16px rgba(97,97,97,0.15)
    `,
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
    transform: "translateY(-1px)",
  },
};

const ShortRequests: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const user       = useSelector((s: RootState) => s.auth.user);
  const userId     = user?.userId || user?.uid || null;
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);

  const requests  = useSelector((s: RootState) => s.shortstayReservations.hostReservationRequests);
  const allUsers  = useSelector((s: RootState) => s.auth.allUsers);
  const listings  = useSelector((s: RootState) => s.shortstays.listings);

  const [expanded,        setExpanded]        = useState<string | null>(null);
  const [loadingId,       setLoadingId]       = useState<string | null>(null);
  const [fetchedListings, setFetchedListings] = useState<string[]>([]);
  const [tab,             setTab]             = useState<"pending" | "approved" | "declined">("pending");

  useEffect(() => {
    if (authLoaded && userId) {
      dispatch(listenToHostReservationRequests(userId));
      dispatch(fetchAllUsers());
    }
  }, [authLoaded, userId, dispatch]);

  useEffect(() => {
    requests.forEach((req) => {
      if (!fetchedListings.includes(req.listingId)) {
        dispatch(listenToShortstayData(req.listingId));
        setFetchedListings((p) => [...p, req.listingId]);
      }
    });
  }, [requests, fetchedListings, dispatch]);

  const getGuest   = (guestId: string) => allUsers.find((u: any) => u.userId === guestId);
  const getListing = (listingId: string) => listings.find((l: any) => l.listingId === listingId);

  const filtered = requests.filter((r) => {
    if (tab === "pending")  return r.isPending && !r.isApproved && !r.isRejected;
    if (tab === "approved") return r.isApproved;
    if (tab === "declined") return r.isRejected;
    return false;
  });

  const pendingCount = requests.filter(
    (r) => r.isPending && !r.isApproved && !r.isRejected
  ).length;

  const handleApprove = async (req: ShortstayRequest) => {
    setLoadingId(req.requestId);
    try { await approveRequest(req.requestId); }
    catch (err) { console.error("Approve error:", err); }
    finally { setLoadingId(null); }
  };

  const handleDecline = async (req: ShortstayRequest) => {
    setLoadingId(req.requestId);
    try { await declineRequest(req.requestId); }
    catch (err) { console.error("Decline error:", err); }
    finally { setLoadingId(null); }
  };

  const StatusBadge = ({ req }: { req: ShortstayRequest }) => {
    if (req.isApproved)
      return (
        <span style={{ ...badge, background: "#F5F5F5", color: "#111", border: "1px solid #E0E0E0" }}>
          Approved
        </span>
      );
    if (req.isRejected)
      return (
        <span style={{ ...badge, background: "#F5F5F5", color: "#888", border: "1px solid #E0E0E0" }}>
          Declined
        </span>
      );
    return (
      <span style={{ ...badge, background: "#111", color: "#fff", border: "1px solid #111" }}>
        Pending
      </span>
    );
  };

  return (
    <>
      <style>{FONT_CSS}</style>

      {/* ── tabs ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, overflowX: "auto", scrollbarWidth: "none" }}>
        {(["pending", "approved", "declined"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "7px 16px",
              borderRadius: 20,
              fontFamily: T.sans,
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all .18s",
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
              ...(tab === t
                ? tabActiveStyles[t]
                : {
                    border: `1.5px solid ${T.border}`,
                    background: T.surface,
                    color: T.muted,
                    boxShadow: "none",
                    textShadow: "none",
                    transform: "none",
                  }),
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === "pending" && pendingCount > 0 && (
              <span
                style={{
                  borderRadius: 10,
                  padding: "1px 7px",
                  fontSize: 10,
                  fontWeight: 600,
                  background: tab === t ? "rgba(255,255,255,0.22)" : "#F1F1F1",
                  color:      tab === t ? "#fff"                   : "#888",
                }}
              >
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── empty state ── */}
      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 20px", color: T.muted }}>
          <Clock size={32} color={T.border2} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 14 }}>No {tab} requests</p>
        </div>
      )}

      {/* ── request cards ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map((req) => {
          const guest     = getGuest(req.guestId);
          const listing   = getListing(req.listingId);
          const nights    = diffNights(req.checkIn, req.checkOut);
          const isOpen    = expanded === req.requestId;
          const busy      = loadingId === req.requestId;
          const firstName = guest?.firstName || "";
          const lastName  = guest?.lastName  || "";
          const initials  = `${firstName[0] || "G"}${lastName[0] || ""}`.toUpperCase();

          return (
            <div
              key={req.requestId}
              style={{
                background:   T.surface,
                border:       `1px solid ${T.border}`,
                borderRadius: 14,
                overflow:     "hidden",
                transition:   "box-shadow .15s",
              }}
            >
              {/* ── card header ── */}
              <div
                onClick={() => setExpanded(isOpen ? null : req.requestId)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer" }}
              >
                {/* listing photo */}
                <div style={{ width: 54, height: 54, borderRadius: 10, flexShrink: 0, background: T.bg, overflow: "hidden", border: `1px solid ${T.border}` }}>
                  {listing?.photos?.[0] ? (
                    <img src={listing.photos[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <MapPin size={18} color={T.muted} />
                    </div>
                  )}
                </div>

                {/* guest avatar + info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    {guest?.profilePicture ? (
                      <img
                        src={guest.profilePicture}
                        alt=""
                        onClick={(e) => { e.stopPropagation(); navigate(`/aboutme/@${guest.username}`); }}
                        style={{
                          width: 28, height: 28, borderRadius: "50%", objectFit: "cover",
                          cursor: "pointer", flexShrink: 0, border: `1.5px solid ${T.border}`,
                        }}
                      />
                    ) : (
                      <div
                        onClick={(e) => { e.stopPropagation(); navigate(`/aboutme/@${guest?.username}`); }}
                        style={{
                          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                          /* 3-D sphere — matches TodayShort avatar */
                          background: "linear-gradient(145deg, #E64A19 0%, #D32F2F 60%, #9B1B1B 100%)",
                          color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700, cursor: "pointer",
                          letterSpacing: "0.04em",
                          border: "1.5px solid rgba(255,255,255,0.55)",
                          boxShadow: `
                            0 1px 0 1px rgba(255,255,255,0.32) inset,
                            0 -1px 0 1px rgba(0,0,0,0.22) inset,
                            0 3px 6px rgba(211,47,47,0.4),
                            0 6px 12px rgba(211,47,47,0.18)
                          `,
                          textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                        }}
                      >
                        {initials[0] || "G"}
                      </div>
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {guest ? `${firstName} ${lastName}`.trim() : "Guest"}
                    </span>
                    <StatusBadge req={req} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.muted }}>
                      <Calendar size={11} />
                      <span>{formatDate(req.checkIn)}</span>
                    </div>
                    <span style={{ fontSize: 11, color: T.border2 }}>→</span>
                    <span style={{ fontSize: 11, color: T.muted }}>{formatDate(req.checkOut)}</span>
                    <span style={{ fontSize: 11, color: T.border2 }}>·</span>
                    <span style={{ fontSize: 11, color: T.muted }}>{nights} night{nights !== 1 ? "s" : ""}</span>
                  </div>
                </div>

                <div style={{ color: T.muted, flexShrink: 0 }}>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* ── expanded detail ── */}
              {isOpen && (
                <div style={{ borderTop: `1px solid ${T.border}`, padding: "16px 16px 18px", background: T.bg }}>
                  {listing && (
                    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 2 }}>
                        {listing.title || "Listing"}
                      </p>
                      {listing.address && (
                        <p style={{ fontSize: 11, color: T.muted, display: "flex", alignItems: "center", gap: 4 }}>
                          <MapPin size={10} />
                          {[listing.address.street, listing.address.city, listing.address.country].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  )}

                  {[
                    { label: "Check-in",   val: formatDate(req.checkIn) },
                    { label: "Check-out",  val: formatDate(req.checkOut) },
                    { label: "Duration",   val: `${nights} night${nights !== 1 ? "s" : ""}` },
                    { label: "Request ID", val: req.requestId.slice(0, 10) + "…" },
                  ].map(({ label, val }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 12, color: T.muted }}>{label}</span>
                      <span style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{val}</span>
                    </div>
                  ))}

                  {/* view profile */}
                  <button
                    onClick={() => navigate(`/aboutme/@${guest?.username}`)}
                    style={{
                      marginTop: 14, width: "100%", padding: "9px 0",
                      borderRadius: 8, border: `1px solid ${T.border}`,
                      background: T.surface, color: T.text,
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: T.sans,
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    }}
                  >
                    {guest?.profilePicture ? (
                      <img src={guest.profilePicture} style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover" }} alt="" />
                    ) : (
                      <div style={{
                        width: 20, height: 20, borderRadius: "50%",
                        background: "linear-gradient(145deg, #E64A19 0%, #D32F2F 60%, #9B1B1B 100%)",
                        color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700,
                        boxShadow: "0 2px 4px rgba(211,47,47,0.35)",
                        border: "1px solid rgba(255,255,255,0.5)",
                        textShadow: "0 1px 1px rgba(0,0,0,0.3)",
                      }}>
                        {initials[0] || "G"}
                      </div>
                    )}
                    View {guest?.firstName || "guest"}'s profile
                  </button>

                  {/* message guest */}
                  <button
                    onClick={() => navigate(`/messages/${req.guestId}`)}
                    style={{
                      marginTop: 8, width: "100%", padding: "9px 0",
                      borderRadius: 8, border: `1px solid ${T.border2}`,
                      background: "transparent", color: T.textSoft,
                      fontSize: 12, fontWeight: 500, cursor: "pointer",
                      fontFamily: T.sans,
                    }}
                  >
                    Message {guest?.firstName || "guest"}
                  </button>

                  {/* approve / decline */}
                  {req.isPending && !req.isApproved && !req.isRejected && (
                    <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                      <button
                        onClick={() => handleDecline(req)}
                        disabled={busy}
                        style={{
                          flex: 1, padding: "11px 0", borderRadius: 8,
                          border: `1.5px solid ${T.border2}`,
                          background: busy ? T.border : T.surface,
                          color: T.textSoft, fontSize: 13, fontWeight: 600,
                          cursor: busy ? "not-allowed" : "pointer",
                          fontFamily: T.sans,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                          opacity: busy ? 0.6 : 1,
                        }}
                      >
                        <X size={14} />
                        {busy ? "…" : "Decline"}
                      </button>
                      <button
                        onClick={() => handleApprove(req)}
                        disabled={busy}
                        style={{
                          flex: 2, padding: "11px 0", borderRadius: 8,
                          border: "none",
                          background: busy ? T.border2 : "#111",
                          color: "#fff", fontSize: 13, fontWeight: 600,
                          cursor: busy ? "not-allowed" : "pointer",
                          fontFamily: T.sans,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                          opacity: busy ? 0.6 : 1,
                        }}
                      >
                        <Check size={14} />
                        {busy ? "Processing…" : "Approve booking"}
                      </button>
                    </div>
                  )}

                  {req.isApproved && (
                    <div style={{
                      marginTop: 14, padding: "10px 12px", borderRadius: 8,
                      background: "#F5F5F5", border: `1px solid ${T.border}`,
                      display: "flex", alignItems: "center", gap: 8,
                      fontSize: 12, color: "#111", fontWeight: 500,
                    }}>
                      <Check size={14} />
                      You approved this request. The guest can now proceed to payment.
                    </div>
                  )}

                  {req.isRejected && (
                    <div style={{
                      marginTop: 14, padding: "10px 12px", borderRadius: 8,
                      background: "#F5F5F5", border: `1px solid ${T.border}`,
                      display: "flex", alignItems: "center", gap: 8,
                      fontSize: 12, color: "#888", fontWeight: 500,
                    }}>
                      <X size={14} />
                      You declined this request.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ShortRequests;

const badge: React.CSSProperties = {
  display:       "inline-flex",
  alignItems:    "center",
  padding:       "3px 9px",
  borderRadius:  20,
  fontSize:      10,
  fontWeight:    600,
  letterSpacing: "0.04em",
  whiteSpace:    "nowrap",
};